import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import config from 'config'
import api from 'utils/api'
import { buildUrl } from 'utils/helpers'
import { showErrorMessage, showInfoMessage } from 'store/ui/actions'

export const fetchMasterKeys = createAsyncThunk(
  'masterKeys/fetch',
  async (
    { page = 1, perPage = 25, search = '' },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const payload = await api.request(
        buildUrl('/master_keys', {
          page,
          per_page: perPage, // eslint-disable-line camelcase
          search: search || undefined,
        }),
        {},
        config.baseUri
      )
      return { ...payload, page }
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const syncEntityLists = createAsyncThunk(
  'masterKeys/syncEntityLists',
  async ({ uuid, entityListIds }, { rejectWithValue, dispatch }) => {
    try {
      const payload = await api.request(
        `/master_keys/${uuid}/sync_entity_lists`,
        {
          method: 'PATCH',
          body: JSON.stringify({ entity_list_ids: entityListIds }), // eslint-disable-line camelcase
        },
        config.baseUri
      )
      return payload
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const createMasterKey = createAsyncThunk(
  'masterKeys/create',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const payload = await api.request(
        '/master_keys',
        {
          method: 'POST',
          body: JSON.stringify({ master_key: data }), // eslint-disable-line camelcase
        },
        config.baseUri
      )
      return payload.data?.master_key
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const updateMasterKey = createAsyncThunk(
  'masterKeys/update',
  async ({ uuid, ...data }, { rejectWithValue, dispatch }) => {
    try {
      const payload = await api.request(
        `/master_keys/${uuid}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ master_key: data }), // eslint-disable-line camelcase
        },
        config.baseUri
      )
      return payload.data?.master_key
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const deleteMasterKey = createAsyncThunk(
  'masterKeys/delete',
  async (uuid, { rejectWithValue, dispatch }) => {
    try {
      const payload = await api.request(
        `/master_keys/${uuid}`,
        { method: 'DELETE' },
        config.baseUri
      )
      return payload
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const importMasterKeys = createAsyncThunk(
  'masterKeys/import',
  async (csv, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData()
      formData.append('file', csv)
      const payload = await api.request(
        '/master_keys/import',
        {
          method: 'POST',
          body: formData,
          headers: new Headers(),
        },
        config.baseUri
      )
      dispatch(showInfoMessage(payload?.message))
      return payload
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

export const fetchMasterKey = createAsyncThunk(
  'masterKeys/fetchOne',
  async (uuid, { rejectWithValue, dispatch }) => {
    try {
      const payload = await api.request(
        `/master_keys/${uuid}`,
        {},
        config.baseUri
      )
      return payload.data.master_key
    } catch (err) {
      const { error } = await err.json()
      dispatch(showErrorMessage(error))
      return rejectWithValue(error)
    }
  }
)

const initialState = {
  list: [],
  meta: { total: 0, page: 1, perPage: 25, totalPages: 1 },
  pending: false,
  pendingOne: false,
  pendingSync: false,
  pendingSave: false,
  pendingDelete: false,
  pendingImport: false,
  currentKey: null,
  search: '',
}

const masterKeysSlice = createSlice({
  name: 'masterKeys',
  initialState,
  reducers: {
    setSearch: (state, { payload }) => {
      state.search = payload
    },
    clearMasterKeys: () => initialState,
    clearCurrentKey: (state) => {
      state.currentKey = null
    },
  },
  extraReducers: {
    [fetchMasterKeys.pending]: (state) => {
      state.pending = true
    },
    [fetchMasterKeys.fulfilled]: (state, { payload }) => {
      state.pending = false
      state.list = payload.data || []
      // eslint-disable-next-line camelcase
      const {
        total_count: total = 0,
        page = 1,
        per_page: perPage = 25,
        total_pages: totalPages = 1,
      } = payload.metadata.meta || {}
      state.meta = { total, page, perPage, totalPages }
    },
    [fetchMasterKeys.rejected]: (state) => {
      state.pending = false
    },
    [fetchMasterKey.pending]: (state) => {
      state.pendingOne = true
    },
    [fetchMasterKey.fulfilled]: (state, { payload }) => {
      state.pendingOne = false
      state.currentKey = payload
    },
    [fetchMasterKey.rejected]: (state) => {
      state.pendingOne = false
    },
    [createMasterKey.pending]: (state) => {
      state.pendingSave = true
    },
    [createMasterKey.fulfilled]: (state) => {
      state.pendingSave = false
    },
    [createMasterKey.rejected]: (state) => {
      state.pendingSave = false
    },
    [updateMasterKey.pending]: (state) => {
      state.pendingSave = true
    },
    [updateMasterKey.fulfilled]: (state) => {
      state.pendingSave = false
    },
    [updateMasterKey.rejected]: (state) => {
      state.pendingSave = false
    },
    [deleteMasterKey.pending]: (state) => {
      state.pendingDelete = true
    },
    [deleteMasterKey.fulfilled]: (state) => {
      state.pendingDelete = false
    },
    [deleteMasterKey.rejected]: (state) => {
      state.pendingDelete = false
    },
    [syncEntityLists.pending]: (state) => {
      state.pendingSync = true
    },
    [syncEntityLists.fulfilled]: (state) => {
      state.pendingSync = false
    },
    [syncEntityLists.rejected]: (state) => {
      state.pendingSync = false
    },
    [importMasterKeys.pending]: (state) => {
      state.pendingImport = true
    },
    [importMasterKeys.fulfilled]: (state) => {
      state.pendingImport = false
    },
    [importMasterKeys.rejected]: (state) => {
      state.pendingImport = false
    },
  },
})

export const { setSearch, clearMasterKeys, clearCurrentKey } =
  masterKeysSlice.actions
export default masterKeysSlice.reducer
